'use client';
import React, { useEffect, useRef, useCallback } from 'react';
import { WidgetType } from './widgetType';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useUser } from '@benzinga/user-context';
import { isGlobalImpressionStored, storeGlobalImpression } from '@benzinga/content-manager';
import { queryFreestarStickyFooterElement } from '@benzinga/ads-utils';
import { isMobile } from '@benzinga/device-utils';
import styles from './floatingWidget.module.scss';

declare global {
  interface Window {
    hj?: () => void;
  }
}

interface FloatingWNSTNWidgetProps {
  widgetType?: WidgetType;
  questions?: string[] | null;
  articleID?: number;
  symbol?: string;
  isCrypto?: boolean;
  assetType?: 'CRYPTO' | 'ETF' | 'FX' | 'INDEX' | 'STOCK';
}

interface ChatParams {
  language: string;
  proxy: string;
  userId: string;
  article_index?: string;
  follow_up_questions?: string;
  assetType?: string;
  assetSymbol?: string;
}

export const FloatingWNSTNWidget: React.FC<FloatingWNSTNWidgetProps> = ({
  articleID,
  assetType,
  questions: initialQuestions,
  symbol,
  widgetType = WidgetType.FollowUp,
}) => {
  const impressed = useRef<boolean>(false);
  const floatingWidgetContainerRef = useRef<HTMLDivElement>(null);
  const initialIframeSizeRef = useRef<{ width: number; height: number } | null>(null);
  const viewTrackingObserverRef = useRef<MutationObserver | null>(null);
  const mountedRef = useRef(true);

  const session = React.useContext(SessionContext);
  const user = useUser();

  const checkForFooterAndAdjustPosition = useCallback(() => {
    if (!floatingWidgetContainerRef.current) return;

    const connatixElement = document.querySelector('.cnx-float-position-bottom');
    if (connatixElement && isMobile()) {
      floatingWidgetContainerRef.current.style.display = 'none';
      return;
    } else if (floatingWidgetContainerRef.current.style.display === 'none' && !connatixElement) {
      floatingWidgetContainerRef.current.style.display = '';
    }

    const widgetContainer = floatingWidgetContainerRef.current.querySelector(
      '.my-chat-container-container__widget',
    ) as HTMLElement;

    const chatContainer = floatingWidgetContainerRef.current.querySelector('.my-chat-container') as HTMLElement;

    let isFullscreen = false;
    let isTallPanel = false;
    if (widgetContainer) {
      const rect = widgetContainer.getBoundingClientRect();
      const computedStyle = getComputedStyle(widgetContainer);
      const iframe = widgetContainer.querySelector('iframe');
      const iframeRect = iframe?.getBoundingClientRect();

      isFullscreen =
        computedStyle.height === '100dvh' || computedStyle.height === '100vh' || rect.height === window.innerHeight;
      isTallPanel = (iframeRect?.height ?? 0) > 300;
    }

    floatingWidgetContainerRef.current.classList.toggle(styles.expanded, isFullscreen || isTallPanel);

    if (isFullscreen) {
      if (chatContainer) {
        chatContainer.style.bottom = '0px';
      }
      return;
    }

    const activeFooter = queryFreestarStickyFooterElement();

    if (activeFooter && chatContainer) {
      const footerRect = activeFooter.getBoundingClientRect();
      const footerHeight = footerRect.height;
      const isFooterVisible = footerRect.top < window.innerHeight && footerRect.bottom > 0;
      const isFooterSticky =
        activeFooter.classList.contains('adthrive-sticky') ||
        activeFooter.classList.contains('fs-sticky-footer') ||
        activeFooter.id === 'fs-sticky-footer' ||
        activeFooter.id === 'fs-slot-footer-wrapper';

      if (isFooterVisible && isFooterSticky) {
        const newOffset = footerHeight + 10;
        chatContainer.style.bottom = `calc(${newOffset}px + env(safe-area-inset-bottom, 0px))`;
      } else {
        chatContainer.style.bottom = '0px';
      }
    } else if (chatContainer) {
      chatContainer.style.bottom = '0px';
    }
  }, []);

  const handleImpress = useCallback(() => {
    if (!impressed.current && floatingWidgetContainerRef?.current?.style.display !== 'none') {
      impressed.current = true;
      session.getManager(TrackingManager).trackWnstnEvent('wnstn_floating_widget_view', {
        page_link: window.location.href,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuestions = useCallback(async (articleId: number | string): Promise<string[]> => {
    if (!articleId) return [];

    try {
      const baseUrl = 'https://editorial-tools.benzinga.com/api/v2/wnstn/';
      const response = await fetch(`${baseUrl}followups/${articleId}`, {
        credentials: 'include',
      });
      if (!response.ok) return [];
      const data = await response.json();
      if (data?.status === 'success' && Array.isArray(data.follow_up_questions)) {
        return data.follow_up_questions;
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const loadWidget = useCallback(async () => {
    const accessType = user?.accessType === 'subscribed' || user?.accessType === 'trialing' ? 'pro' : 'www';
    const chatParams: ChatParams = {
      language: 'EN',
      proxy: `https://wnstn-api.benzinga.com/${accessType}`,
      userId: String(user?.id || ''),
    };

    if (widgetType === WidgetType.FollowUp && articleID) {
      chatParams.article_index = String(articleID);

      let questions = initialQuestions;
      if (!questions || questions.length === 0) {
        questions = await fetchQuestions(articleID);
      }

      if (questions && questions.length > 0) {
        chatParams.follow_up_questions = questions.join(';');
      }
    }

    if (widgetType === WidgetType.Asset) {
      if (assetType) {
        chatParams.assetType = assetType;
      }
      if (symbol) {
        chatParams.assetSymbol = symbol;
      }
    }

    window['chatInitParams'] = { ...chatParams };

    const widgetContainer = floatingWidgetContainerRef?.current?.querySelector('.my-chat-container-container__widget');
    if (widgetContainer) {
      widgetContainer.innerHTML = '';
      const script = document.createElement('script');
      script.id = 'wnstn-widget-script';
      script.src = 'https://staticfiles.wnstn.ai/jsfiles/createFloatingWidget.js';
      script.defer = true;
      script.onload = () => {
        const attachViewTrackingAndErrorHandler = (iframe: HTMLIFrameElement) => {
          if (!initialIframeSizeRef.current) {
            const rect = iframe.getBoundingClientRect();
            initialIframeSizeRef.current = { height: rect.height, width: rect.width };
          }
          handleImpress();
          iframe.onerror = error => {
            console.error('Floating WNSTN Widget Error:', error);
            iframe.remove();
            floatingWidgetContainerRef?.current?.remove();
          };
        };

        const existingIframe = widgetContainer.querySelector('iframe');
        if (existingIframe) {
          attachViewTrackingAndErrorHandler(existingIframe as HTMLIFrameElement);
        } else {
          const viewTrackingObserver = new MutationObserver(() => {
            const iframe = widgetContainer.querySelector('iframe');
            if (iframe && mountedRef.current) {
              viewTrackingObserver.disconnect();
              viewTrackingObserverRef.current = null;
              attachViewTrackingAndErrorHandler(iframe as HTMLIFrameElement);
            }
          });
          viewTrackingObserverRef.current = viewTrackingObserver;
          viewTrackingObserver.observe(widgetContainer, { childList: true, subtree: true });
        }

        setTimeout(checkForFooterAndAdjustPosition, 100);
      };

      const existingScript = document.getElementById('wnstn-widget-script');
      if (existingScript) {
        existingScript.remove();
      }

      widgetContainer.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrackWidgetHover = useCallback(() => {
    if (isGlobalImpressionStored('FLOATING_WNSTN')) return;
    try {
      const trackingManager = session?.getManager(TrackingManager);
      if (trackingManager) {
        storeGlobalImpression('FLOATING_WNSTN');
        trackingManager.trackWnstnEvent('wnstn_floating_widget_hover', {
          node_id: articleID ? String(articleID) : undefined,
          page_link: window.location.href,
          symbol,
        });
      }
    } catch (error) {
      console.error('Error tracking floating WNSTN widget hover:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hotjarWidgetMutationObserver = new MutationObserver((_mutations, observer) => {
      const widget = document.querySelector('._hj-widget-container');
      if (widget) {
        observer.disconnect();
      }
      if (widget && floatingWidgetContainerRef.current) {
        floatingWidgetContainerRef.current.style.display = 'none';
      }
    });

    hotjarWidgetMutationObserver.observe(document.body, { childList: true, subtree: true });
  }, []);

  useEffect(() => {
    const idleCallbackId =
      typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback(loadWidget) : undefined;
    const timeoutId = idleCallbackId === undefined ? window.setTimeout(loadWidget, 250) : undefined;

    return () => {
      mountedRef.current = false;
      if (idleCallbackId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      if (viewTrackingObserverRef.current) {
        viewTrackingObserverRef.current.disconnect();
        viewTrackingObserverRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkForFooterAndAdjustPosition();

    const footerMutationObserver = new MutationObserver(() => {
      checkForFooterAndAdjustPosition();
    });

    footerMutationObserver.observe(document.body, {
      attributeFilter: ['class', 'style'],
      attributes: true,
      childList: true,
      subtree: true,
    });

    const handleScrollResize = () => {
      checkForFooterAndAdjustPosition();
    };

    window.addEventListener('scroll', handleScrollResize, { passive: true });
    window.addEventListener('resize', handleScrollResize, { passive: true });

    return () => {
      footerMutationObserver.disconnect();
      window.removeEventListener('scroll', handleScrollResize);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [checkForFooterAndAdjustPosition]);

  useEffect(() => {
    let resizeObserver: ResizeObserver;
    let mutationObserver: MutationObserver;

    const setupObservers = () => {
      const widgetContainer = floatingWidgetContainerRef.current?.querySelector(
        '.my-chat-container-container__widget',
      ) as HTMLElement;

      if (!widgetContainer) return;

      const maybeTrackWidgetExpand = () => {
        if (isGlobalImpressionStored('FLOATING_WNSTN')) return;
        const iframe = widgetContainer.querySelector('iframe');
        if (!iframe || !initialIframeSizeRef.current) return;
        const rect = iframe.getBoundingClientRect();
        const initial = initialIframeSizeRef.current;
        const threshold = 10;
        if (rect.width > initial.width + threshold || rect.height > initial.height + threshold) {
          handleTrackWidgetHover();
        }
      };

      resizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          checkForFooterAndAdjustPosition();
          maybeTrackWidgetExpand();
        }, 100);
      });

      resizeObserver.observe(widgetContainer);

      mutationObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            setTimeout(() => {
              checkForFooterAndAdjustPosition();
              maybeTrackWidgetExpand();
            }, 50);
          }
        });
      });

      mutationObserver.observe(widgetContainer, {
        attributeFilter: ['style'],
        attributes: true,
      });
    };

    const widgetContainer = floatingWidgetContainerRef.current?.querySelector('.my-chat-container-container__widget');
    if (widgetContainer) {
      setupObservers();
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [checkForFooterAndAdjustPosition, handleTrackWidgetHover]);

  return (
    <div className={styles.FloatingWidgetContainer} ref={floatingWidgetContainerRef}>
      <div className="my-chat-container">
        <div className="my-chat-container-container__widget" onMouseOver={handleTrackWidgetHover} />
      </div>
    </div>
  );
};

export default FloatingWNSTNWidget;
